import React, { useEffect, useRef, useState } from 'react';
import { supabase } from '../../../lib/supabaseClient';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, BarChart, Bar } from 'recharts';
import { FaChartLine } from 'react-icons/fa';
import toast from 'react-hot-toast';

interface Stats {
  total_clicks: number;
  last_clicked_at: string | null;
  daily_clicks: { date: string; clicks: number }[];
  top_referrers: { referrer: string; clicks: number }[];
  top_utm_source?: { utm_source: string; clicks: number }[];
  top_devices?: { device: string; clicks: number }[];
  top_os?: { os: string; clicks: number }[];
  top_browsers?: { browser: string; clicks: number }[];
  top_countries?: { country: string; clicks: number }[];
}

const UrlShortenerTab: React.FC = () => {
  const [url, setUrl] = useState('');
  const [customCode, setCustomCode] = useState('');
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<{ full_url: string; short_code: string } | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [sendWhatsAppOnCreate, setSendWhatsAppOnCreate] = useState(false);
  const [milestoneThreshold, setMilestoneThreshold] = useState('');
  const statsRef = useRef<HTMLDivElement | null>(null);

  const validateUrl = (value: string) => {
    try {
      const u = new URL(value);
      return ['http:', 'https:'].includes(u.protocol);
    } catch {
      return false;
    }
  };

  const downloadCsv = async (shortCode: string) => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token || '';
      const res = await fetch(`/api/url/${encodeURIComponent(shortCode)}/events.csv`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || 'Failed to download CSV');
      }
      const blob = await res.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `events_${shortCode}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('CSV downloaded');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to download CSV');
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateUrl(url)) {
      toast.error('Please enter a valid URL starting with http(s)://');
      return;
    }

    setLoading(true);
    setSuccess(null);
    setStats(null);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token || '';
      const alerts = (sendWhatsAppOnCreate || (milestoneThreshold && Number(milestoneThreshold) > 0))
        ? {
            on_create: sendWhatsAppOnCreate,
            milestone_threshold: milestoneThreshold ? Number(milestoneThreshold) : undefined
          }
        : undefined;

      const res = await fetch('/api/url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          original_url: url,
          custom_code: customCode || undefined,
          title: title || undefined,
          alerts
        })
      });

      if (res.status === 429) {
        const data = await res.json().catch(() => ({}));
        toast.error(data?.message || 'Monthly limit reached (10 links/month)');
        return;
      }

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast.error(data?.error || 'Failed to create short link');
        return;
      }

      const data = await res.json();
      setSuccess({ full_url: data.full_url, short_code: data.short_code });
      setUrl('');
      setCustomCode('');
      setTitle('');
      setSendWhatsAppOnCreate(false);
      setMilestoneThreshold('');
      toast.success('Short link created');
    } catch (e: any) {
      toast.error(e?.message || 'Unexpected error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadStats = async () => {
      if (!success?.short_code) return;
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData?.session?.access_token || '';
        const res = await fetch(`/api/url/stats/${encodeURIComponent(success.short_code)}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined
        });
        if (!res.ok) return;
        const data = await res.json();
        setStats({
          total_clicks: data.total_clicks ?? 0,
          last_clicked_at: data.last_clicked_at ?? null,
          daily_clicks: Array.isArray(data.daily_clicks) ? data.daily_clicks : [],
          top_referrers: Array.isArray(data.top_referrers) ? data.top_referrers : [],
          top_utm_source: Array.isArray(data.top_utm_source) ? data.top_utm_source : [],
          top_devices: Array.isArray(data.top_devices) ? data.top_devices : [],
          top_os: Array.isArray(data.top_os) ? data.top_os : [],
          top_browsers: Array.isArray(data.top_browsers) ? data.top_browsers : [],
          top_countries: Array.isArray(data.top_countries) ? data.top_countries : [],
        });
      } catch {
        // Silently ignore
      }
    };
    loadStats();
  }, [success?.short_code]);

  return (
    <div className="space-y-6">
      {/* Info Box */}
      <div className="rounded-xl bg-blue-50 p-5 shadow-sm ring-1 ring-blue-100">
        <h3 className="mb-2 text-base font-semibold text-blue-900">Create Trackable Short Links</h3>
        <p className="text-sm text-blue-800">
          Shorten long URLs and track clicks with detailed analytics including referrers, devices, and geographic data.
        </p>
      </div>

      {/* Success Message */}
      {success && (
        <div className="rounded-xl bg-green-50 p-5 shadow-sm ring-1 ring-green-200">
          <div className="mb-2 text-sm font-semibold text-green-900">✓ Short link created</div>
          <div className="flex items-center gap-2">
            <a
              href={success.full_url}
              target="_blank"
              rel="noreferrer"
              className="text-sm font-medium text-blue-600 hover:underline break-all"
            >
              {success.full_url}
            </a>
            <button
              onClick={() => {
                navigator.clipboard.writeText(success.full_url);
                toast.success('Copied to clipboard');
              }}
              className="flex items-center gap-1 rounded-lg bg-blue-600 px-3 py-1 text-xs text-white transition hover:bg-blue-700"
            >
              📋 Copy
            </button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              onClick={() => statsRef.current?.scrollIntoView({ behavior: 'smooth' })}
              className="flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-1 text-xs text-white transition hover:bg-indigo-700"
            >
              <FaChartLine /> View Analytics
            </button>
            <button
              onClick={() => downloadCsv(success.short_code)}
              className="flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1 text-xs text-white transition hover:bg-emerald-700"
            >
              📥 Export CSV
            </button>
          </div>
        </div>
      )}

      {/* Analytics */}
      {stats && (
        <div ref={statsRef} className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-200">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Analytics (Last 30 Days)</h3>
            <div className="text-sm text-gray-600">
              Total clicks: <span className="font-bold text-primary">{stats.total_clicks}</span>
            </div>
          </div>

          <div className="space-y-6">
            {/* Daily Clicks Chart */}
            <div>
              <h4 className="mb-2 text-sm font-medium text-gray-700">Daily Clicks</h4>
              {stats.daily_clicks.length === 0 ? (
                <div className="text-xs text-gray-500">No clicks yet</div>
              ) : (
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={stats.daily_clicks} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="clicks" name="Clicks" stroke="#3b82f6" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* Grid of Bar Charts */}
            <div className="grid gap-4 md:grid-cols-2">
              {/* Top Referrers */}
              <div>
                <h4 className="mb-2 text-sm font-medium text-gray-700">Top Referrers</h4>
                {stats.top_referrers.length === 0 ? (
                  <div className="text-xs text-gray-500">No data</div>
                ) : (
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={stats.top_referrers.map(r => ({ name: r.referrer || 'direct', clicks: r.clicks }))}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-20} height={50} />
                        <YAxis allowDecimals={false} />
                        <Tooltip />
                        <Bar dataKey="clicks" fill="#10b981" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>

              {/* Devices */}
              <div>
                <h4 className="mb-2 text-sm font-medium text-gray-700">Devices</h4>
                {(!stats.top_devices || stats.top_devices.length === 0) ? (
                  <div className="text-xs text-gray-500">No data</div>
                ) : (
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={stats.top_devices.map(r => ({ name: r.device || 'unknown', clicks: r.clicks }))}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis allowDecimals={false} />
                        <Tooltip />
                        <Bar dataKey="clicks" fill="#f59e0b" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>

              {/* Countries */}
              <div>
                <h4 className="mb-2 text-sm font-medium text-gray-700">Countries</h4>
                {(!stats.top_countries || stats.top_countries.length === 0) ? (
                  <div className="text-xs text-gray-500">No data</div>
                ) : (
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={stats.top_countries.map(r => ({ name: r.country || 'unknown', clicks: r.clicks }))}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis allowDecimals={false} />
                        <Tooltip />
                        <Bar dataKey="clicks" fill="#06b6d4" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>

              {/* Browsers */}
              <div>
                <h4 className="mb-2 text-sm font-medium text-gray-700">Browsers</h4>
                {(!stats.top_browsers || stats.top_browsers.length === 0) ? (
                  <div className="text-xs text-gray-500">No data</div>
                ) : (
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={stats.top_browsers.map(r => ({ name: r.browser || 'unknown', clicks: r.clicks }))}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis allowDecimals={false} />
                        <Tooltip />
                        <Bar dataKey="clicks" fill="#22c55e" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={onSubmit} className="rounded-xl bg-gray-50 p-5 shadow-sm ring-1 ring-gray-200">
        <h3 className="mb-4 text-base font-semibold text-gray-900">Create Short Link</h3>
        
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Destination URL *</label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com/your-long-url"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              required
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Custom Code</label>
              <input
                type="text"
                value={customCode}
                onChange={(e) => setCustomCode(e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 10))}
                placeholder="my-campaign"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
              <span className="mt-1 block text-xs text-gray-500">3-10 alphanumeric. Leave blank for auto-generated</span>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="September Campaign"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
          </div>

          {/* WhatsApp Alerts */}
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-800">
              💬 WhatsApp Alerts
            </h4>
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={sendWhatsAppOnCreate}
                  onChange={(e) => setSendWhatsAppOnCreate(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <span>Notify me when this link is created</span>
              </label>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Milestone Alert (clicks)</label>
                <input
                  type="number"
                  min={1}
                  value={milestoneThreshold}
                  onChange={(e) => setMilestoneThreshold(e.target.value)}
                  placeholder="e.g. 100"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
                <span className="mt-1 block text-xs text-gray-500">Get alerted when clicks reach this number</span>
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary/90 disabled:opacity-50"
          >
            {loading ? 'Creating...' : 'Create Short Link'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UrlShortenerTab;
